import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from "recharts";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../ui/select";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import {
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Calendar,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Inbox,
  Activity,
  Reply
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_COLOURS = {
  Highest: "#C23A00",
  High: "#6B5600",
  Medium: "#FFD108",
  Low: "#0E6B3F",
  Lowest: "#8A6D00",
  Unspecified: "#8A857A"
};
const SERIES_COLOURS = ["#8A6D00", "#0E6B3F", "#6B5600", "#C23A00", "#1D4ED8", "#3B82F6", "#FFD108"];

const PERIOD_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "this_week", label: "This Week" },
  { value: "last_week", label: "Last Week" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "this_year", label: "This Year" },
  { value: "last_year", label: "Last Year" },
  { value: "custom", label: "Custom Range" }
];

function StatCard({ icon: Icon, label, value, sub, accent, delta, testid }) {
  return (
    <div
      className="bg-[#FFFFFF] border border-[#DAD5CA] rounded-lg p-4"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-xs text-[#59554D] mb-2">
        <Icon className="w-4 h-4" style={{ color: accent }} />
        {label}
      </div>
      <div className="text-2xl font-semibold text-[#1A1A1A]">
        {value ?? "—"}
      </div>
      {sub && <div className="text-xs text-[#8A857A] mt-1">{sub}</div>}
      {delta !== undefined && delta !== null && (
        <div
          className={`text-xs mt-1 flex items-center gap-1 ${
            delta > 0 ? "text-[#0E6B3F]" : delta < 0 ? "text-[#C23A00]" : "text-[#8A857A]"
          }`}
        >
          {delta > 0 ? <TrendingUp className="w-3 h-3" /> : delta < 0 ? <TrendingDown className="w-3 h-3" /> : null}
          {delta > 0 ? "+" : ""}{delta}% vs previous
        </div>
      )}
    </div>
  );
}

function pctDelta(curr, prev) {
  if (prev === 0 || prev == null) return null;
  if (curr == null) return null;
  return Math.round(((curr - prev) / prev) * 100);
}

export default function TicketReportsView() {
  const [period, setPeriod] = useState("this_week");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [compare, setCompare] = useState(true);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  const runFetch = (p, cs, ce, cmp) => {
    if (p === "custom" && (!cs || !ce)) return;
    const myId = ++requestIdRef.current;
    setLoading(true);
    const params = { period: p, compare: cmp };
    if (p === "custom") {
      params.start = `${cs}T00:00:00`;
      params.end = `${ce}T23:59:59`;
    }
    axios
      .get(`${API}/analytics/ticket-reports`, { params })
      .then((res) => {
        // Ignore stale responses (out-of-order resolution)
        if (myId !== requestIdRef.current) return;
        setReport(res.data);
      })
      .catch((e) => {
        if (myId !== requestIdRef.current) return;
        console.error("ticket-reports failed", e);
      })
      .finally(() => {
        if (myId !== requestIdRef.current) return;
        setLoading(false);
      });
  };

  useEffect(() => {
    runFetch(period, customStart, customEnd, compare);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, customStart, customEnd, compare]);

  const refresh = () => runFetch(period, customStart, customEnd, compare);

  if (!report && loading) {
    return (
      <div className="py-16 text-center text-[#59554D]" data-testid="ticket-reports-loading">
        <RefreshCw className="w-6 h-6 mx-auto mb-3 animate-spin" />
        Loading ticket report…
      </div>
    );
  }

  const r = report || {};
  const totals = r.totals || {};
  const prev = r.previous || null;
  const prevTotals = prev?.totals || {};

  // Format dates short
  const shortDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("en-AU", { day: "2-digit", month: "short" });
  };

  const dailyData = (r.daily || []).map((d) => ({
    date: shortDate(d.date),
    Created: d.created,
    Closed: d.closed
  }));

  const priorityData = (r.by_priority || []).map((p) => ({
    name: p.key,
    value: p.value,
    fill: PRIORITY_COLOURS[p.key] || "#8A857A"
  }));

  const statusData = (r.by_status || []).map((s, idx) => ({
    name: s.key,
    value: s.value,
    fill: SERIES_COLOURS[idx % SERIES_COLOURS.length]
  }));

  const slaData = r.sla && (r.sla.breached + r.sla.on_time > 0)
    ? [
        { name: "On-time", value: r.sla.on_time, fill: "#0E6B3F" },
        { name: "Breached", value: r.sla.breached, fill: "#C23A00" }
      ]
    : [];

  return (
    <div className="space-y-6" data-testid="ticket-reports-view">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger
            className="w-[180px] bg-[#F4F2ED] border-[#DAD5CA]"
            data-testid="ticket-reports-period"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
            {PERIOD_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {period === "custom" && (
          <>
            <Input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="w-[160px] bg-[#F4F2ED] border-[#DAD5CA]"
              data-testid="ticket-reports-custom-start"
            />
            <span className="text-[#8A857A]">to</span>
            <Input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="w-[160px] bg-[#F4F2ED] border-[#DAD5CA]"
              data-testid="ticket-reports-custom-end"
            />
          </>
        )}

        <div className="flex items-center gap-2 bg-[#F4F2ED] border border-[#DAD5CA] rounded-md px-3 py-2">
          <Switch
            checked={compare}
            onCheckedChange={setCompare}
            className="data-[state=checked]:bg-[#FFD108] data-[state=unchecked]:bg-[#C9C3B6]"
            data-testid="ticket-reports-compare-toggle"
          />
          <span className="text-xs text-[#59554D]">Compare to previous</span>
        </div>

        <Button
          variant="outline"
          onClick={refresh}
          disabled={loading}
          className="border-[#DAD5CA]"
          data-testid="ticket-reports-refresh"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>

        {r.label && (
          <Badge className="bg-[#FFF8D6] text-[#8A6D00] hover:bg-[#FFF8D6]">
            {r.label} · {shortDate(r.start)} – {shortDate(r.end)} · {totals.span_days} day{totals.span_days === 1 ? "" : "s"}
          </Badge>
        )}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard
          icon={Inbox}
          label="Tickets created"
          value={totals.created ?? 0}
          accent="#8A6D00"
          delta={compare && prev ? pctDelta(totals.created, prevTotals.created) : null}
          testid="stat-created"
        />
        <StatCard
          icon={CheckCircle2}
          label="Tickets closed"
          value={totals.closed ?? 0}
          accent="#0E6B3F"
          delta={compare && prev ? pctDelta(totals.closed, prevTotals.closed) : null}
          testid="stat-closed"
        />
        <StatCard
          icon={Activity}
          label="Avg tickets / day"
          value={totals.avg_tickets_per_day ?? 0}
          sub={`over ${totals.span_days || 0} day${totals.span_days === 1 ? "" : "s"}`}
          accent="#1D4ED8"
          delta={compare && prev ? pctDelta(totals.avg_tickets_per_day, prevTotals.avg_tickets_per_day) : null}
          testid="stat-avg-per-day"
        />
        <StatCard
          icon={Clock}
          label="Avg resolution"
          value={totals.avg_resolution_hours != null ? `${totals.avg_resolution_hours}h` : "—"}
          sub={totals.avg_resolution_hours != null && totals.avg_resolution_hours > 48
            ? `${Math.round(totals.avg_resolution_hours / 24)} days avg`
            : null}
          accent="#6B5600"
          testid="stat-avg-resolution"
        />
        <StatCard
          icon={Inbox}
          label="Open at end"
          value={totals.open_at_end ?? 0}
          accent="#FFD108"
          testid="stat-open-at-end"
        />
        <StatCard
          icon={AlertTriangle}
          label="SLA compliance"
          value={r.sla?.compliance_pct != null ? `${r.sla.compliance_pct}%` : "—"}
          sub={r.sla
            ? `${r.sla.breached} breached / ${r.sla.on_time} on-time`
            : null}
          accent={r.sla?.compliance_pct >= 90 ? "#0E6B3F" : r.sla?.compliance_pct >= 75 ? "#6B5600" : "#C23A00"}
          testid="stat-sla"
        />
        <StatCard
          icon={Reply}
          label={`First response (${r.first_response?.sla_hours ?? 1}h SLA)`}
          value={r.first_response?.avg_hours != null ? `${r.first_response.avg_hours}h` : "—"}
          sub={r.first_response
            ? (r.first_response.compliance_pct != null
                ? `${r.first_response.compliance_pct}% within SLA · ${r.first_response.awaiting} awaiting`
                : `${r.first_response.awaiting} awaiting reply`)
            : null}
          accent={
            r.first_response?.compliance_pct >= 90 ? "#0E6B3F" :
            r.first_response?.compliance_pct >= 75 ? "#6B5600" :
            r.first_response?.compliance_pct != null ? "#C23A00" : "#1D4ED8"
          }
          testid="stat-first-response"
        />
      </div>

      {/* Created vs Closed daily line chart */}
      <div className="bg-[#FFFFFF] border border-[#DAD5CA] rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-[#1A1A1A] flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#8A6D00]" />
            Tickets per day — Created vs Closed
          </h3>
          <div className="text-xs text-[#8A857A]">
            Avg incoming: <span className="text-[#1A1A1A] font-medium">{totals.avg_tickets_per_day ?? 0}</span> / day
          </div>
        </div>
        {dailyData.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#DAD5CA" />
              <XAxis dataKey="date" stroke="#8A857A" tick={{ fontSize: 11 }} />
              <YAxis stroke="#8A857A" tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: "#F4F2ED", border: "1px solid #DAD5CA", borderRadius: 8 }}
                labelStyle={{ color: "#59554D" }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="Created" stroke="#8A6D00" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="Closed" stroke="#0E6B3F" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="py-12 text-center text-[#8A857A] text-sm">No daily data for this period</div>
        )}
      </div>

      {/* Priority + Status + SLA donuts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-[#FFFFFF] border border-[#DAD5CA] rounded-lg p-4">
          <h3 className="text-sm font-medium text-[#1A1A1A] mb-3">By Priority (created in period)</h3>
          {priorityData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={priorityData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={50}
                  outerRadius={90}
                  paddingAngle={2}
                  label={(e) => `${e.name}: ${e.value}`}
                  labelLine={false}
                >
                  {priorityData.map((p, i) => (
                    <Cell key={i} fill={p.fill} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: "#F4F2ED", border: "1px solid #DAD5CA", borderRadius: 8 }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="py-12 text-center text-[#8A857A] text-sm">No data</div>
          )}
        </div>

        <div className="bg-[#FFFFFF] border border-[#DAD5CA] rounded-lg p-4">
          <h3 className="text-sm font-medium text-[#1A1A1A] mb-3">Open Tickets by Status (at end)</h3>
          {statusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={statusData} layout="vertical" margin={{ left: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#DAD5CA" />
                <XAxis type="number" stroke="#8A857A" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis dataKey="name" type="category" stroke="#8A857A" tick={{ fontSize: 11 }} width={140} />
                <Tooltip
                  contentStyle={{ background: "#F4F2ED", border: "1px solid #DAD5CA", borderRadius: 8 }}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {statusData.map((s, i) => (
                    <Cell key={i} fill={s.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="py-12 text-center text-[#8A857A] text-sm">No data</div>
          )}
        </div>

        <div className="bg-[#FFFFFF] border border-[#DAD5CA] rounded-lg p-4">
          <h3 className="text-sm font-medium text-[#1A1A1A] mb-3">SLA compliance</h3>
          {slaData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={slaData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={50}
                  outerRadius={90}
                  paddingAngle={2}
                  label={(e) => `${e.name}: ${e.value}`}
                  labelLine={false}
                >
                  {slaData.map((s, i) => (
                    <Cell key={i} fill={s.fill} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: "#F4F2ED", border: "1px solid #DAD5CA", borderRadius: 8 }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="py-12 text-center text-[#8A857A] text-sm">No SLA data in this period</div>
          )}
        </div>
      </div>

      {/* By Agent table */}
      <div className="bg-[#FFFFFF] border border-[#DAD5CA] rounded-lg p-4">
        <h3 className="text-sm font-medium text-[#1A1A1A] mb-3">Top Agents (by tickets closed)</h3>
        {(r.by_agent || []).length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[#59554D] border-b border-[#DAD5CA]">
                  <th className="py-2 pr-4 font-medium">Agent</th>
                  <th className="py-2 pr-4 font-medium text-right">Created</th>
                  <th className="py-2 pr-4 font-medium text-right">Closed</th>
                  <th className="py-2 font-medium">Closed share</th>
                </tr>
              </thead>
              <tbody>
                {r.by_agent.map((a) => {
                  const denom = totals.closed || 1;
                  const share = Math.round((a.closed / denom) * 100);
                  return (
                    <tr key={a.key} className="border-b border-[#DAD5CA]" data-testid={`agent-row-${a.key}`}>
                      <td className="py-2 pr-4 text-[#1A1A1A]">{a.key}</td>
                      <td className="py-2 pr-4 text-right">{a.created}</td>
                      <td className="py-2 pr-4 text-right">{a.closed}</td>
                      <td className="py-2">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-[#F4F2ED] rounded overflow-hidden max-w-[160px]">
                            <div
                              className="h-full bg-[#0E6B3F]"
                              style={{ width: `${share}%` }}
                            />
                          </div>
                          <span className="text-xs text-[#8A857A] w-10 text-right">{share}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-8 text-center text-[#8A857A] text-sm">No agent data for this period</div>
        )}
      </div>

      {/* By Organization */}
      <div className="bg-[#FFFFFF] border border-[#DAD5CA] rounded-lg p-4">
        <h3 className="text-sm font-medium text-[#1A1A1A] mb-3">Top Organizations (by tickets created)</h3>
        {(r.by_organization || []).length > 0 ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={r.by_organization.map((o) => ({ name: o.key, value: o.value }))} layout="vertical" margin={{ left: 80 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#DAD5CA" />
              <XAxis type="number" stroke="#8A857A" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis dataKey="name" type="category" stroke="#8A857A" tick={{ fontSize: 11 }} width={160} />
              <Tooltip
                contentStyle={{ background: "#F4F2ED", border: "1px solid #DAD5CA", borderRadius: 8 }}
              />
              <Bar dataKey="value" fill="#8A6D00" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="py-8 text-center text-[#8A857A] text-sm">No organization data for this period</div>
        )}
      </div>

      {/* Idle */}
      {r.idle && (r.idle.tickets_with_idle_hours > 0) && (
        <div className="bg-[#FFFFFF] border border-[#DAD5CA] rounded-lg p-4 flex items-center gap-6">
          <div>
            <div className="text-xs text-[#59554D] flex items-center gap-1">
              <Clock className="w-3 h-3" /> Idle metrics for tickets created in period
            </div>
            <div className="text-2xl font-semibold text-[#1A1A1A] mt-1" data-testid="stat-idle-count">
              {r.idle.tickets_with_idle_hours}
            </div>
            <div className="text-xs text-[#8A857A]">tickets currently idle</div>
          </div>
          <div className="h-10 w-px bg-[#DAD5CA]" />
          <div>
            <div className="text-xs text-[#59554D]">Average idle hours</div>
            <div className="text-2xl font-semibold text-[#1A1A1A] mt-1">
              {r.idle.avg_idle_hours != null ? `${r.idle.avg_idle_hours}h` : "—"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
